import { useAtomValue } from "jotai";
import take from "lodash/take";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { notEmpty } from "shared/lib/utils";

import { useTracking } from "scmp-app/components/article/article-recirculation-widget/hooks";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import type { furtherReadingWidgetArticle$key } from "scmp-app/queries/__generated__/furtherReadingWidgetArticle.graphql";

import { Container, StyledContentItemArticleWidget, Title } from "./styles";

type Props = {
  className?: string;
  reference: furtherReadingWidgetArticle$key;
};

export const ArticleFurtherReadingWidget: FunctionComponent<Props> = ({ className, reference }) => {
  const article = useFragment(
    graphql`
      fragment furtherReadingWidgetArticle on Article {
        moreOnThisArticles {
          entityId
          ...articleWidgetContent
        }
      }
    `,
    reference,
  );

  const asyncRosettaState = useAtomValue(rosettaAtom);

  const isSubscriber = useMemo(
    () => asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false,
    [asyncRosettaState?.result?.user?.isSCMPSubscriber],
  );
  const moreOnThisArticles = useMemo(
    () => take(article.moreOnThisArticles?.filter(notEmpty) ?? [], isSubscriber ? 4 : 3),
    [article.moreOnThisArticles, isSubscriber],
  );

  const { captureImpressionTrackingElement, handleClickTracking } = useTracking({
    getClickCustomizedParameters: () => ({
      page_type: "article",
      widget_name: "read_more",
      widget_version: isSubscriber ? "subscriber" : "non_subscriber",
    }),
    getImpressionCustomizedParameters: () => ({
      list_of_articles: moreOnThisArticles.map(item => item.entityId).join(","),
      page_type: "article",
      widget_name: "read_more",
      widget_version: isSubscriber ? "subscriber" : "non_subscriber",
    }),
  });

  if (!notEmpty(moreOnThisArticles)) return null;
  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "further_reading_RM",
        pgtype: "article",
      }}
    >
      <Container className={className}>
        <Title ref={captureImpressionTrackingElement}>Further Reading</Title>
        {moreOnThisArticles.map((node, index) => (
          <StyledContentItemArticleWidget
            key={index}
            onClick={handleClickTracking}
            reference={node}
            withCoverImage={false}
          />
        ))}
      </Container>
    </BaseLinkContextProvider>
  );
};

ArticleFurtherReadingWidget.displayName = "ArticleFurtherReadingWidget";
